/**
 * English strings for the `settings` namespace. The source of truth: French is
 * typed against this file. See `apps/web/src/i18n/README.md`.
 */
const settings = {
  workspace: {
    title: "Settings",
    intro:
      "Chapters and chat run on your own model credential. We proxy the calls and never bill you for inference.",
    provider: {
      title: "Model provider",
      anthropicHint: "A short, curated list",
      openrouterHint: "Hundreds of models, live pricing",
    },
    apiKey: {
      label: "API key",
      ends: "ends {{hint}}",
      remove: "Remove key",
      replacePlaceholder: "Enter a new key to replace it",
      help: "Verified with {{provider}} before it is stored, then encrypted. It is never sent to the browser again.",
    },
    model: {
      label: "Model",
      needsKey: "Add a key above to browse {{provider}}'s models.",
      filter_one: "Filter {{count}} model…",
      filter_other: "Filter {{count}} models…",
      free: "Free",
      context: "{{size}}k context",
      pricing: "Prices are USD per million input / output tokens.",
    },
    removeConfirm: {
      question:
        "Remove this key? It is deleted from our database, and chapters, summaries and chat stop working until you add another.",
      kept: "Summaries and chapters you have already generated are kept — you paid for those and they stay.",
      keep: "Keep it",
      failed: "Could not remove that key.",
    },
    saveFailed: "Could not save that key.",
    saveCredential: "Save credential",
    savedVerified: "Saved and verified",
    general: {
      title: "Workspace",
      botName: "Notetaker name",
      botNamePlaceholder: "Meeting Notetaker",
      botNameHelp:
        "Every participant sees this in the meeting list. Recording people without an obvious notice is not something this product does, so make it recognisable.",
      retention: "Delete recordings after",
      retentionPlaceholder: "Never",
      days: "days",
      retentionInvalid: "Enter a whole number of days, or leave it blank.",
      retentionHelp:
        "Leave blank to keep recordings forever. This deletes the video and its transcript permanently — there is no undo.",
      saved: "Saved",
    },
  },
  account: {
    title: "Your account",
    intro: "These settings follow you across every workspace you belong to.",
    signedInAs: "Signed in as",
    platformOperator: "Platform operator",
    languageHelp: "Used for the interface and the emails we send you.",
    password: {
      title: "Password",
      body: "Changing it sends a link to your email address. That is the same path as forgetting it, so there is only one way for a password to be set.",
      sendReset: "Send a reset link",
    },
    twoFactor: {
      title: "Two-factor authentication",
      body: "A code from your phone in addition to your password. Recordings of your meetings are as sensitive as the meetings were.",
      turnOn: "Turn it on",
      turnOff: "Turn it off",
      confirmPassword: "Confirm your password",
      enable: "Turn on two-factor",
      disable: "Turn off two-factor",
      disabled: "Two-factor authentication is off.",
      savedTitle: "Two-factor is on — save these now",
      savedBody:
        "Add this to your authenticator app, and keep the backup codes somewhere safe. This is the only time either is shown.",
      setupKey: "Setup key",
      backupCodes: "Backup codes",
      savedThem: "I have saved them",
    },
  },
  members: {
    title: "People",
    intro:
      "Roles apply to this workspace only — the same person can hold a different role elsewhere.",
    rejected: "That change was rejected.",
    suspended: "Suspended",
    removeTitle: "Remove from workspace",
    removeAria: "Remove {{email}}",
    removeOwner: {
      title: "Remove an owner from this workspace?",
      body: "{{name}} is an owner. Removing them takes away their ownership and their access in one step, and only another owner could ever bring them back. Their recordings stay.",
      confirm: "Yes, remove this owner",
    },
    removeMember:
      "Remove <strong>{{email}}</strong> from this workspace? They lose access immediately. Their recordings stay.",
    promote: {
      title: "Make {{name}} an owner?",
      body: "Owners have full control of this workspace, including over other admins, other owners, and over you.",
      confirm: "Yes, make them an owner",
    },
    demote: {
      title: "Take ownership away from {{name}}?",
      body: "They become {{role}} and lose control over people and settings. The workspace must always keep at least one owner.",
      confirm: "Yes, take ownership away",
    },
    /** A role as it reads in the middle of a sentence ("They become admin…"). */
    roleInSentence: {
      user: "member",
      creator: "creator",
      admin: "admin",
      super_admin: "owner",
    },
    rolesTitle: "What the roles mean",
    roleDescription: {
      user: "Read-only: watch recordings, search, and use chat",
      creator: "The above, plus creating and editing — recordings, Studio, bots",
      admin: "The above, plus inviting people and managing creators and members",
      super_admin: "Full control, including over admins and other owners",
    },
    invite: {
      title: "Invite someone",
      body: "They get an email with a link to set a password and join this workspace.",
      email: "Email",
      emailPlaceholder: "colleague@company.com",
      role: "Role",
      send: "Send invitation",
      failed: "Could not send that invitation.",
      resent: "Invitation resent to {{email}}",
      sent: "Invited {{email}}",
      waiting: "Waiting to accept",
      roleExpired: "{{role}} · expired",
      expired: "Expired",
      revoke: "Revoke",
    },
  },
} as const;

export default settings;
